import { useCallback, useId, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, RadioButton, SlideOut, SlideOutHeader, Skeleton } from '@aimsos/ui';

import { useResolveConflict } from '../../../hooks/useResolveConflict.js';
import { useSandboxConflict } from '../../../hooks/useSandboxConflict.js';
import ItemUnavailableState from '../../item-slideout/ItemUnavailableState.jsx';
import { ErrorState } from '../../states/index.js';
import ConflictStatement from './ConflictStatement.jsx';

import { Scale } from 'lucide-react';

const UNSURE = 'unsure';

/**
 * Compare (CT-5718): "two people said different things". What each one said,
 * then "Which one is true?" — one option per person and "Not sure, send for
 * confirmation" — and Confirm this version, disabled until one is picked.
 *
 * Registered as the `claim_compare` kind of the profile slide-out host
 * (`?item=claim_compare:<claimId>`). Resolving tells the page (`onChanged`), so
 * the Sandbox list and How to approach refresh, and closes.
 *
 * Only whoever may confirm the claim (`canConfirm`) gets the choice; anyone else
 * reads what each one said. Each option's text selects it (`<label htmlFor>`).
 */
function ConflictCompareSlideOut({ itemId, entityType, entityId, onClose, onChanged }) {
  const { t } = useTranslation('unified-profiles');
  const questionId = useId();
  const optionIdPrefix = useId();
  const [choice, setChoice] = useState(null);
  const { conflict, loading, error, notFound, refetch } = useSandboxConflict({ entityType, entityId, claimId: itemId });

  const handleResolved = useCallback(() => {
    onChanged?.();
    onClose();
  }, [onChanged, onClose]);
  const { resolve, resolving } = useResolveConflict({ entityType, entityId, claimId: itemId, onResolved: handleResolved });
  const handleConfirm = useCallback(() => resolve(choice), [resolve, choice]);

  const title = conflict?.label ?? t('knowledge.sandbox.compare.fallbackTitle');

  const renderHeader = useCallback(
    (titleId) => (
      <SlideOutHeader
        titleId={titleId}
        title={title}
        subtitle={t('knowledge.sandbox.compare.subtitle')}
        icon={Scale}
        size="M"
        showClose
        onClose={onClose}
      />
    ),
    [title, onClose, t],
  );

  const body = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-3" aria-busy>
          <Skeleton width="60%" height="16px" borderRadius="4px" />
          <Skeleton width="100%" height="64px" borderRadius="8px" />
          <Skeleton width="100%" height="64px" borderRadius="8px" />
        </div>
      );
    }
    if (notFound) return <ItemUnavailableState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!conflict) return null;

    const options = [
      ...conflict.statements.map((statement) => ({
        id: statement.id,
        label: t('knowledge.sandbox.compare.version', { name: statement.person }),
        hint: statement.value,
      })),
      { id: UNSURE, label: t('knowledge.sandbox.compare.unsure'), hint: t('knowledge.sandbox.compare.unsureHint') },
    ];

    return (
      <div data-testid="conflict-compare" className="flex flex-col gap-6">
        <div>
          <Badge variant="error" size="small">
            {t('knowledge.sandbox.status.conflict')}
          </Badge>
        </div>
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">{t('knowledge.sandbox.compare.whatEachSaid')}</h3>
          {conflict.statements.map((statement) => (
            <ConflictStatement key={statement.id} statement={statement} />
          ))}
        </section>
        {conflict.canConfirm ? (
          <section className="flex flex-col gap-3">
            <h3 id={questionId} className="text-sm font-semibold text-content">
              {t('knowledge.sandbox.compare.question')}
            </h3>
            <div role="radiogroup" aria-labelledby={questionId} className="flex flex-col gap-2">
              {options.map((option) => {
                const optionId = `${optionIdPrefix}-${option.id}`;
                return (
                  <div key={option.id} className="flex items-start gap-3">
                    <RadioButton
                      id={optionId}
                      checked={choice === option.id}
                      onChange={() => setChoice(option.id)}
                      disabled={resolving}
                      aria-label={[option.label, option.hint].filter(Boolean).join(' — ')}
                    />
                    <label htmlFor={optionId} className="flex cursor-pointer flex-col text-sm">
                      <span className="text-content">{option.label}</span>
                      {option.hint ? <span className="text-xs text-content-muted">{option.hint}</span> : null}
                    </label>
                  </div>
                );
              })}
            </div>
          </section>
        ) : (
          <p className="text-sm text-content-muted">{t('knowledge.sandbox.compare.readOnly')}</p>
        )}
      </div>
    );
  };

  return (
    <SlideOut isOpen onClose={onClose} renderHeader={renderHeader} size="M" ariaLabel={title}>
      <div className="flex h-full min-h-0 flex-col gap-4">
        <div className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto">{body()}</div>
        {conflict?.canConfirm ? (
          <div className="flex shrink-0 justify-end border-t border-edge pt-4">
            <Button variant="primary" size="sm" onClick={handleConfirm} disabled={!choice || resolving}>
              {t('knowledge.sandbox.compare.confirm')}
            </Button>
          </div>
        ) : null}
      </div>
    </SlideOut>
  );
}

export default ConflictCompareSlideOut;
